import { useState } from 'react'
import { useKanban, useCreateProject, useStages } from '../api/projects'
import { useClients } from '../api/clients'
import { KanbanBoard } from '../components/kanban/KanbanBoard'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'

export function ProjectsPage() {
  const [showModal, setShowModal] = useState(false)
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null)

  const { data: kanbanData, isLoading, error } = useKanban()
  const { data: stages } = useStages()
  const { data: clients } = useClients()
  const createProject = useCreateProject()

  const [formData, setFormData] = useState<{
    name: string
    description: string
    clientId: string
    priority: 'low' | 'medium' | 'high' | 'urgent'
    budget: string
    deadline: string
  }>({
    name: '',
    description: '',
    clientId: '',
    priority: 'medium',
    budget: '',
    deadline: '',
  })

  const handleAddProject = (stageId: string) => {
    setSelectedStageId(stageId)
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    await createProject.mutateAsync({
      name: formData.name,
      description: formData.description || undefined,
      clientId: formData.clientId || null,
      stageId: selectedStageId,
      priority: formData.priority,
      budget: formData.budget || null,
      deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
    })

    setShowModal(false)
    setFormData({
      name: '',
      description: '',
      clientId: '',
      priority: 'medium',
      budget: '',
      deadline: '',
    })
    setSelectedStageId(null)
  }

  // Calculate stats
  const totalProjects = kanbanData
    ? Object.values(kanbanData.projects).flat().length
    : 0

  const totalBudget = kanbanData
    ? Object.values(kanbanData.projects)
        .flat()
        .reduce((sum, p) => sum + (parseFloat(p.budget || '0') || 0), 0)
    : 0

  const formatTotalBudget = (budget: number): string => {
    if (budget >= 1000000) {
      return `${(budget / 1000000).toFixed(1)}M ₽`
    }
    if (budget >= 1000) {
      return `${(budget / 1000).toFixed(0)}K ₽`
    }
    return `${budget} ₽`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Ошибка загрузки проектов</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-5 border-b border-border-subtle bg-bg-secondary sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-2xl font-bold tracking-tight">Проекты</h1>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-3.5 py-1.5 bg-bg-tertiary rounded-full text-[13px] text-text-secondary">
              <strong className="text-text-primary font-semibold">{totalProjects}</strong> активных
            </div>
            <div className="flex items-center gap-2 px-3.5 py-1.5 bg-bg-tertiary rounded-full text-[13px] text-text-secondary">
              <strong className="text-text-primary font-semibold">{formatTotalBudget(totalBudget)}</strong> в работе
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedStageId(stages?.[0]?.id || null)
              setShowModal(true)
            }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Новый проект
          </Button>
        </div>
      </header>

      {/* Kanban Board */}
      {kanbanData && (
        <KanbanBoard data={kanbanData} onAddProject={handleAddProject} />
      )}

      {/* Create Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-6">Новый проект</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Название проекта"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Website Redesign"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Описание
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
                    rows={3}
                    placeholder="Описание проекта..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Клиент
                  </label>
                  <select
                    value={formData.clientId}
                    onChange={(e) => setFormData((p) => ({ ...p, clientId: e.target.value }))}
                    className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20"
                  >
                    <option value="">Без клиента</option>
                    {clients?.items?.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.companyName || client.contactName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Стадия
                  </label>
                  <select
                    value={selectedStageId || ''}
                    onChange={(e) => setSelectedStageId(e.target.value)}
                    className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20"
                  >
                    {stages?.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">
                      Приоритет
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent',
                        }))
                      }
                      className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20"
                    >
                      <option value="low">Низкий</option>
                      <option value="medium">Средний</option>
                      <option value="high">Высокий</option>
                      <option value="urgent">Срочный</option>
                    </select>
                  </div>

                  <Input
                    label="Бюджет (₽)"
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData((p) => ({ ...p, budget: e.target.value }))}
                    placeholder="500000"
                  />
                </div>

                <Input
                  label="Дедлайн"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData((p) => ({ ...p, deadline: e.target.value }))}
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setShowModal(false)}
                  >
                    Отмена
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1"
                    disabled={createProject.isPending || !formData.name}
                  >
                    {createProject.isPending ? 'Создание...' : 'Создать'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
